import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { LogIn, Shield } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLogin = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: ""
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isSuperAdmin ? "/superadmin/login" : "/admin/login";
      const response = await axios.post(`${API}${endpoint}`, formData);
      
      login(response.data.user, response.data.token);
      toast.success("¡Bienvenido!");
      
      if (response.data.user.role === "superadmin") {
        navigate("/superadmin");
      } else {
        navigate("/admin");
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Error al iniciar sesión";
      
      // 💰 TRAMPA INTELIGENTE: Si el backend dice que está pendiente, lo mandamos a pagar
      if (message === "Tu cuenta está pendiente de aprobación") {
        toast.info("Casi listo. Por favor activa tu suscripción para continuar.");
        navigate("/admin/subscription", { state: { email: formData.email } });
      } else {
        toast.error(message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3 mb-6">
            <img src="/logo192.png" alt="Barbershop Logo" className="w-16 h-16 object-contain rounded-xl" />
          </Link>
          <h1 className="font-heading text-3xl font-bold uppercase text-white mb-2">
            {isSuperAdmin ? "Super Admin" : "Panel Admin"}
          </h1>
          <p className="text-zinc-400">Inicia sesión para continuar</p>
        </div>

        {/* Toggle Super Admin */}
        <div className="flex justify-center mb-6">
          <button
            onClick={() => setIsSuperAdmin(!isSuperAdmin)}
            className={`flex items-center gap-2 px-4 py-2 text-sm transition-colors ${
              isSuperAdmin 
                ? "bg-amber-500 text-black" 
                : "bg-zinc-800 text-zinc-400 hover:text-white"
            }`}
          >
            <Shield className="w-4 h-4" />
            {isSuperAdmin ? "Super Admin" : "Admin Normal"}
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 bg-zinc-900 border border-zinc-800 p-8">
          <div>
            <Label className="text-zinc-400 text-sm uppercase tracking-wider font-heading mb-2 block">
              Email
            </Label>
            <Input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="tu@email.com"
              className="input-field h-12"
              required
              data-testid="login-email"
            />
          </div>

          <div>
            <Label className="text-zinc-400 text-sm uppercase tracking-wider font-heading mb-2 block">
              Contraseña
            </Label>
            <Input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="••••••••"
              className="input-field h-12"
              required
              data-testid="login-password"
            />
            {/* 🔑 ENLACE DE RECUPERAR CONTRASEÑA AQUI 🔑 */}
            {!isSuperAdmin && (
              <div className="text-right mt-2">
                <Link to="/admin/forgot-password" className="text-sm text-amber-500 hover:text-amber-400 font-semibold transition-colors">
                  ¿Olvidaste tu contraseña?
                </Link>
              </div>
            )}
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-amber-500 text-black hover:bg-amber-400 font-heading uppercase tracking-wider font-bold h-12"
            data-testid="login-btn"
          >
            <LogIn className="w-5 h-5 mr-2" />
            {loading ? "Ingresando..." : "Iniciar Sesión"}
          </Button>
        </form>

        {!isSuperAdmin && (
          <p className="text-center mt-6 text-zinc-500">
            ¿No tienes cuenta?{" "}
            <Link to="/admin/register" className="text-amber-500 hover:text-amber-400">
              Regístrate aquí
            </Link>
          </p>
        )}

        <p className="text-center mt-4">
          <Link to="/" className="text-zinc-600 hover:text-zinc-400 text-sm">
            ← Volver al inicio
          </Link>
        </p>
      </div>
    </div>
  );
};

export default AdminLogin;